import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TagItemInDTO } from 'app/fuse-layouts/layout1/type';
import { ListObject } from 'app/main/design-system/models/listObjectsModel';
import { ClientsSortOrderEnum } from 'app/main/pages/clients/type';
import {
  FranchiseStatusEnum,
  MerchantAndStore,
  MerchantAndStoreSortEnum,
  PartnerFilterEnum,
} from '../../admin/types/type';
import { PosMerchantAccountDTO, SalesReceptionStatusDTO, StoreDetails, StoreDTO, StoreSettingsDTO } from '../type';
import storesAdapter from './stores.adapter';

export const PAGE = 1;
export const ROWS_PER_PAGE = 20;
export const CLIENTS_SORT_DEFAULT = MerchantAndStoreSortEnum.DATE_CREATED;
export const CLIENTS_SORT_ORDER_DEFAULT = ClientsSortOrderEnum.descending;
export const CLIENTS_STATUS_DEFAULT = FranchiseStatusEnum.ALL;
export const CLIENTS_PARTNER_DEFAULT = PartnerFilterEnum.ALL;

export interface StoreState {
  ids: string[];
  entities: { [key: string]: StoreDetails };
  currentStoreId?: string;
  routeParams: any;
  merchantAndStores: {
    merchantAndStores: MerchantAndStore[];
    filter?: string;
    statusFilter: FranchiseStatusEnum;
    partnerFilter: PartnerFilterEnum;
    hasLoadedFilterSortPagination: boolean;
    sort: {
      type?: MerchantAndStoreSortEnum;
      order?: ClientsSortOrderEnum;
    };
    pagination: {
      page: number;
      rowsPerPage: number;
    };
    isLoading: boolean;
    codeError: number | null;
  };

  storeDialog: {
    type: 'new' | 'admin' | 'edit';
    isOpen: boolean;
    isLoading: boolean;
    data?: StoreDTO;
    dialogType?: 'store' | 'merchant';
    userId?: string;
  };
  confirmDeleteDialog: {
    id: string | null;
    storeName: string | null;
    isOpen: boolean;
    isLoading: boolean;
  };
  posMerchantAccount?: PosMerchantAccountDTO;
  salesReceptionStatus: SalesReceptionStatusDTO | null;

  posDialog: {
    isOpen: boolean;
    isLoading: boolean;
  };
  tagsDialog: {
    isOpen: boolean;
    isLoading: boolean;
  };
  isSettingsLoading: boolean;
}

const initialState: StoreState = {
  ids: [],
  entities: {},
  routeParams: {},
  currentStoreId: undefined,
  merchantAndStores: {
    merchantAndStores: [],
    isLoading: false,
    hasLoadedFilterSortPagination: false,
    codeError: null,
    statusFilter: FranchiseStatusEnum.ALL,
    partnerFilter: CLIENTS_PARTNER_DEFAULT,
    sort: {
      type: CLIENTS_SORT_DEFAULT,
      order: CLIENTS_SORT_ORDER_DEFAULT,
    },
    pagination: {
      page: PAGE,
      rowsPerPage: ROWS_PER_PAGE,
    },
  },
  salesReceptionStatus: null,
  storeDialog: {
    type: 'new',
    isOpen: false,
    isLoading: false,
    data: undefined,
    userId: undefined,
  },
  confirmDeleteDialog: {
    id: null,
    storeName: null,
    isOpen: false,
    isLoading: false,
  },
  posDialog: {
    isOpen: false,
    isLoading: false,
  },
  tagsDialog: {
    isOpen: false,
    isLoading: false,
  },
  isSettingsLoading: false,
};

export const { actions, reducer } = createSlice({
  name: 'storesApp/stores',
  initialState: storesAdapter.getInitialState(initialState),
  reducers: {
    openNewStoreDialog: (state: StoreState, action: PayloadAction<string>) => {
      state.storeDialog = {
        ...state.storeDialog,
        type: 'new',
        isOpen: true,
        isLoading: false,
        data: undefined,
        dialogType: 'merchant',
        userId: action.payload,
      };
    },
    openNewStoreAdminDialog: (state: StoreState, action: PayloadAction<{ isFirst: boolean; userId?: string }>) => {
      state.storeDialog = {
        ...state.storeDialog,
        type: action.payload.isFirst ? 'admin' : 'new',
        isOpen: true,
        isLoading: false,
        data: undefined,
        dialogType: 'store',
        userId: action.payload.userId,
      };
    },
    openEditStoreDialog: (state, action) => {
      state.storeDialog = {
        ...state.storeDialog,
        type: 'edit',
        isOpen: true,
        isLoading: false,
        data: action.payload,
      };
    },
    closeStoreDialog: state => {
      state.storeDialog = {
        ...state.storeDialog,
        type: 'new',
        isOpen: false,
      };
    },
    setStoreDialogIsLoading: (state: StoreState, action: PayloadAction<Partial<boolean>>) => {
      state.storeDialog = {
        ...state.storeDialog,
        isLoading: action.payload,
      };
    },
    openConfirmDeleteDialog: (state, action) => {
      state.confirmDeleteDialog = {
        id: action.payload.id,
        storeName: action.payload.name,
        isOpen: true,
        isLoading: false,
      };
    },
    setConfirmDeleteDialogIsLoading: (state, action: PayloadAction<boolean>) => {
      state.confirmDeleteDialog = {
        ...state.confirmDeleteDialog,
        isLoading: action.payload,
      };
    },
    closeConfirmDeleteDialog: state => {
      state.confirmDeleteDialog = {
        id: null,
        storeName: null,
        isOpen: false,
        isLoading: false,
      };
    },
    getStoresSuccess: (state, action) => {
      const { data, routeParams } = action.payload;
      storesAdapter.setAll(state, data);
      state.routeParams = routeParams;
    },
    getStoresFailed: state => {
      storesAdapter.setAll(state, []);
    },
    setMerchantAndStores: (state, action: PayloadAction<MerchantAndStore[]>) => {
      state.merchantAndStores.merchantAndStores = action.payload;
    },
    setMerchantAndStoresFilter: (state: StoreState, action: PayloadAction<string>) => {
      state.merchantAndStores.filter = action.payload;
    },
    setMerchantAndStoresSort: (
      state: StoreState,
      action: PayloadAction<Partial<StoreState['merchantAndStores']['sort']>>,
    ) => {
      state.merchantAndStores.sort = { ...state.merchantAndStores.sort, ...action.payload };
    },
    setMerchantAndStoresStatusFilter: (
      state: StoreState,
      action: PayloadAction<StoreState['merchantAndStores']['statusFilter']>,
    ) => {
      state.merchantAndStores.statusFilter = action.payload;
    },
    setMerchantAndStoresPartnerFilter: (state: StoreState, action: PayloadAction<PartnerFilterEnum>) => {
      state.merchantAndStores.partnerFilter = action.payload;
    },
    setMerchantAndStoresPagination: (
      state: StoreState,
      action: PayloadAction<Partial<StoreState['merchantAndStores']['pagination']>>,
    ) => {
      state.merchantAndStores.pagination = { ...state.merchantAndStores.pagination, ...action.payload };
    },
    setMerchantAndStoresFilterSortPagination: (
      state: StoreState,
      action: PayloadAction<{
        filter: string;
        statusFilter: StoreState['merchantAndStores']['statusFilter'];
        partnerFilter: StoreState['merchantAndStores']['partnerFilter'];
        sort: StoreState['merchantAndStores']['sort'];
        pagination: StoreState['merchantAndStores']['pagination'];
      }>,
    ) => {
      state.merchantAndStores.filter = action.payload.filter;
      state.merchantAndStores.statusFilter = action.payload.statusFilter;
      state.merchantAndStores.partnerFilter = action.payload.partnerFilter;
      state.merchantAndStores.sort = action.payload.sort;
      state.merchantAndStores.pagination = action.payload.pagination;
      state.merchantAndStores.hasLoadedFilterSortPagination = true;
    },
    setMerchantAndStoresIsLoading: (state: StoreState, action: PayloadAction<boolean>) => {
      state.merchantAndStores.isLoading = action.payload;
    },
    setMerchantAndStoresCodeError: (state: StoreState, action: PayloadAction<number | null>) => {
      state.merchantAndStores.codeError = action.payload;
    },
    updateMerchantAndStores: (state, action) => {
      const indexMerchantToUpdate = state.merchantAndStores.merchantAndStores.findIndex(
        entry => entry.id === action.payload.userId,
      );

      state.merchantAndStores.merchantAndStores[indexMerchantToUpdate].isSubscribedRetailer =
        action.payload.subscription.isSubscribedRetailer;
      state.merchantAndStores.merchantAndStores[indexMerchantToUpdate].hasFullAccess =
        action.payload.subscription.hasFullAccess;
      state.merchantAndStores.merchantAndStores[indexMerchantToUpdate].hasAcceptedCGV =
        action.payload.subscription.hasAcceptedCGV;
    },
    updateMerchantAndStoresIsLoading: (
      state: StoreState,
      action: PayloadAction<{ userId: string; isLoading: boolean }>,
    ) => {
      const indexMerchantToUpdate = state.merchantAndStores.merchantAndStores.findIndex(
        entry => entry.id === action.payload.userId,
      );

      state.merchantAndStores.merchantAndStores[indexMerchantToUpdate].isLoading = action.payload.isLoading;
      state.merchantAndStores.merchantAndStores = [...state.merchantAndStores.merchantAndStores];
    },
    setPosMerchantAccount: (state: StoreState, action: PayloadAction<PosMerchantAccountDTO | null>) => {
      state.posMerchantAccount = action.payload || undefined;
    },
    setSalesReceptionStatus: (state: StoreState, action: PayloadAction<SalesReceptionStatusDTO | null>) => {
      state.salesReceptionStatus = action.payload;
    },

    openPosDialog: (state: StoreState) => {
      state.posDialog = {
        isOpen: true,
        isLoading: false,
      };
    },
    closePosDialog: (state: StoreState) => {
      state.posDialog = {
        isOpen: false,
        isLoading: false,
      };
    },
    setPosDialogIsLoading: (state: StoreState, action: PayloadAction<boolean>) => {
      state.posDialog = {
        ...state.posDialog,
        isLoading: action.payload,
      };
    },
    openTagsDialog: state => {
      state.tagsDialog = {
        isOpen: true,
        isLoading: false,
      };
    },
    closeTagsDialog: state => {
      state.tagsDialog = {
        isOpen: false,
        isLoading: false,
      };
    },
    setTagsDialogIsLoading: (state, action: PayloadAction<boolean>) => {
      state.tagsDialog = {
        ...state.tagsDialog,
        isLoading: action.payload,
      };
    },
    setStoreTagsHasSuccess: (
      state: StoreState,
      action: PayloadAction<{ storeId: string; tags: ListObject<TagItemInDTO> }>,
    ) => {
      storesAdapter.updateOne(state, {
        id: action.payload.storeId,
        changes: {
          tags: {
            data: action.payload.tags.data,
            metadata: action.payload.tags.metadata,
            isLoading: false,
            hasError: false,
          },
        },
      });
    },
    setStoreTagsHasError: (state: StoreState, action: PayloadAction<{ storeId: string; hasError: boolean }>) => {
      storesAdapter.updateOne(state, {
        id: action.payload.storeId,
        changes: {
          tags: {
            data: [],
            metadata: { page: null, size: null, total: 0 },
            isLoading: false,
            hasError: action.payload.hasError,
          },
        },
      });
    },
    setStoreTagsIsLoading: (state: StoreState, action: PayloadAction<{ storeId: string; isLoading: boolean }>) => {
      storesAdapter.updateOne(state, {
        id: action.payload.storeId,
        changes: {
          tags: {
            ...(state.entities[action.payload.storeId].tags || {
              data: [],
              metadata: { page: null, size: null, total: 0 },
            }),
            isLoading: action.payload.isLoading,
            // getStoreTags ends a failed load with isLoading: false, which must keep the error it just recorded.
            hasError: action.payload.isLoading ? false : Boolean(state.entities[action.payload.storeId].tags?.hasError),
          },
        },
      });
    },
    setStoreSettingsIsLoading: (state: StoreState, action: PayloadAction<boolean>) => {
      state.isSettingsLoading = action.payload;
    },
    setStoreSettings: (state: StoreState, action: PayloadAction<{ storeId: string; settings: StoreSettingsDTO }>) => {
      storesAdapter.updateOne(state, {
        id: action.payload.storeId,
        changes: {
          settings: action.payload.settings,
        },
      });
    },
    setCurrentStoreId: (state: StoreState, action: PayloadAction<string>) => {
      state.currentStoreId = action.payload;
    },
  },
});
